import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import api from './services/api';
import logger from './utils/logger';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TwoFactorVerify from './pages/TwoFactorVerify';
import Dashboard from './pages/Dashboard';
import SejoursList from './pages/SejoursList';
import EnfantsList from './pages/EnfantsList';
import InscriptionsList from './pages/InscriptionsList';
import InscriptionsListAdmin from './pages/InscriptionsListAdmin';
import InscriptionDetail from './pages/InscriptionDetail';
import InformationsEnfant from './pages/InformationsEnfant';
import DashboardAdmin from './pages/DashboardAdmin';
import GestionSejours from './pages/GestionSejours';
import GestionPlaces from './pages/GestionPlaces';
import GestionPartenaires from './pages/GestionPartenaires';
import ValidationPartenaires from './pages/ValidationPartenaires';
import InscriptionEnAttenteValidation from './pages/InscriptionEnAttenteValidation';
import AdminInscriptionDetail from './pages/AdminInscriptionDetail';
import GestionMembres from './pages/GestionMembres';
import LogsAdmin from './pages/LogsAdmin';
import BackupsAdmin from './pages/BackupsAdmin';
import GestionEmails from './pages/GestionEmails';
import GestionHelloAsso from './pages/GestionHelloAsso';
import GestionPages from './pages/GestionPages';
import GestionDocuments from './pages/GestionDocuments';
import EditeurPageAccueil from './pages/EditeurPageAccueil';
import EditeurNavbarFooter from './pages/EditeurNavbarFooter';
import CustomPageViewer from './pages/CustomPageViewer';
import DocumentViewer from './pages/DocumentViewer';
import Profil from './pages/Profil';
import MentionsLegales from './pages/MentionsLegales';
import PolitiqueConfidentialite from './pages/PolitiqueConfidentialite';
import PolitiqueRGPDSante from './pages/PolitiqueRGPDSante';
import CGV from './pages/CGV';
import PresentationAssociation from './pages/PresentationAssociation';
import PaiementCallback from './pages/PaiementCallback';
import PrivateRoute from './components/PrivateRoute';
import AdminRoute from './components/AdminRoute';
import Navbar from './components/Navbar';
import Header from './components/Header';
import Footer from './components/Footer';
import NotificationContainer from './components/NotificationContainer';
import PromptContainer from './components/PromptContainer';
import ConfirmContainer from './components/ConfirmContainer';

// Router pour rediriger vers le bon dashboard selon le rôle
const DashboardRouter = () => {
  const { user } = useAuth();

  if (user?.role === 'ADMIN') {
    return <DashboardAdmin />;
  }

  return <Dashboard />;
};

// Router pour la liste des inscriptions selon le rôle
const InscriptionsRouter = () => {
  const { user } = useAuth();

  if (user?.role === 'ADMIN') {
    return <InscriptionsListAdmin />;
  }

  return <InscriptionsList />;
};

// Router pour la page racine - charge la page custom "/" si elle existe
const RootRouter = () => {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [pageContent, setPageContent] = React.useState(null);
  const [navbarContent, setNavbarContent] = React.useState(null);
  const [navbarHtmlWithAuth, setNavbarHtmlWithAuth] = React.useState('');

  React.useEffect(() => {
    const loadHomePage = async () => {
      try {
        // Essayer de charger une page personnalisée pour "/"
        const response = await api.get('/custom-pages/render/', {
          params: { path: '/' }
        });
        setPageContent(response.data);

        // Charger la navbar si include_navbar est activé
        if (response.data.include_navbar) {
          try {
            const navbarResponse = await api.get('/custom-pages/get_navbar/');
            setNavbarContent(navbarResponse.data);
          } catch (navErr) {
            logger.error('Erreur lors du chargement de la navbar:', navErr);
          }
        }

        // Mettre à jour le titre
        if (response.data.meta_title || response.data.title) {
          document.title = response.data.meta_title || response.data.title;
        }
      } catch (err) {
        // Si pas de page custom, on affichera PresentationAssociation
      } finally {
        setLoading(false);
      }
    };

    loadHomePage();
  }, []);

  // Mettre à jour la navbar avec l'état d'authentification
  React.useEffect(() => {
    if (navbarContent?.navbar_html) {
      let html = navbarContent.navbar_html;

      // Mettre à jour le bouton desktop
      if (isAuthenticated) {
        html = html.replace(
          /(<a[^>]*id="desktop-auth-btn"[^>]*>)[^<]*(Se connecter)?[^<]*(<\/a>)/i,
          '$1Mon Espace$3'
        );
        html = html.replace(
          /(<a[^>]*id="desktop-auth-btn"[^>]*href=")[^"]*(")/i,
          '$1/dashboard$2'
        );
      } else {
        html = html.replace(
          /(<a[^>]*id="desktop-auth-btn"[^>]*>)[^<]*(Mon Espace)?[^<]*(<\/a>)/i,
          '$1Se connecter$3'
        );
        html = html.replace(
          /(<a[^>]*id="desktop-auth-btn"[^>]*href=")[^"]*(")/i,
          '$1/login$2'
        );
      }

      // Mettre à jour le bouton mobile
      if (isAuthenticated) {
        html = html.replace(
          /(<a[^>]*id="mobile-auth-btn"[^>]*>)[^<]*(Se connecter)?[^<]*(<\/a>)/i,
          '$1Mon Espace$3'
        );
        html = html.replace(
          /(<a[^>]*id="mobile-auth-btn"[^>]*href=")[^"]*(")/i,
          '$1/dashboard$2'
        );
      } else {
        html = html.replace(
          /(<a[^>]*id="mobile-auth-btn"[^>]*>)[^<]*(Mon Espace)?[^<]*(<\/a>)/i,
          '$1Se connecter$3'
        );
        html = html.replace(
          /(<a[^>]*id="mobile-auth-btn"[^>]*href=")[^"]*(")/i,
          '$1/login$2'
        );
      }

      setNavbarHtmlWithAuth(html);
    }
  }, [navbarContent, isAuthenticated]);

  React.useEffect(() => {
    // Injecter le CSS dans le <head> pour que @import fonctionne
    let styleElement = null;
    if (pageContent?.css_content) {
      try {
        styleElement = document.createElement('style');
        styleElement.id = 'custom-page-styles';
        styleElement.textContent = pageContent.css_content;
        document.head.appendChild(styleElement);
      } catch (err) {
        logger.error('Erreur lors de l\'injection du CSS:', err);
      }
    }

    return () => {
      if (styleElement && styleElement.parentNode) {
        document.head.removeChild(styleElement);
      }
    };
  }, [pageContent?.css_content]);

  React.useEffect(() => {
    // Exécuter le JavaScript de la page après le rendu
    if (pageContent?.js_content) {
      try {
        const script = document.createElement('script');
        script.textContent = pageContent.js_content;
        document.body.appendChild(script);
        return () => {
          document.body.removeChild(script);
        };
      } catch (err) {
        logger.error('Erreur lors de l\'exécution du JavaScript:', err);
      }
    }
  }, [pageContent]);

  React.useEffect(() => {
    // Injecter le CSS de la navbar dans le <head>
    let navbarStyleElement = null;
    if (navbarContent?.navbar_css) {
      try {
        navbarStyleElement = document.createElement('style');
        navbarStyleElement.id = 'navbar-styles';
        navbarStyleElement.textContent = navbarContent.navbar_css;
        document.head.appendChild(navbarStyleElement);
      } catch (err) {
        logger.error('Erreur lors de l\'injection du CSS de la navbar:', err);
      }
    }

    return () => {
      if (navbarStyleElement && navbarStyleElement.parentNode) {
        document.head.removeChild(navbarStyleElement);
      }
    };
  }, [navbarContent?.navbar_css]);

  React.useEffect(() => {
    // Exécuter le JavaScript de la navbar (si présent, hors gestion auth)
    if (navbarContent?.navbar_js) {
      try {
        const navbarScript = document.createElement('script');
        navbarScript.textContent = navbarContent.navbar_js;
        document.body.appendChild(navbarScript);
        return () => {
          if (navbarScript.parentNode) {
            document.body.removeChild(navbarScript);
          }
        };
      } catch (err) {
        logger.error('Erreur lors de l\'exécution du JavaScript de la navbar:', err);
      }
    }
  }, [navbarContent?.navbar_js]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Si une page custom existe pour "/", l'afficher
  if (pageContent) {
    return (
      <div className="min-h-screen flex flex-col">
        {/* Navbar séparée si include_navbar est activé */}
        {pageContent.include_navbar && navbarHtmlWithAuth && (
          <div dangerouslySetInnerHTML={{ __html: navbarHtmlWithAuth }} />
        )}
        <div className="flex-grow" dangerouslySetInnerHTML={{ __html: pageContent.html_content }} />
        {pageContent.include_footer && <Footer noSidebarMargin={true} />}
      </div>
    );
  }

  // Sinon, afficher la page de présentation par défaut
  return <PresentationAssociation />;
};

// Composant wrapper pour les pages authentifiées avec sidebar
const AuthenticatedLayout = ({ children }) => {
  return (
    <>
      <Header />
      <div className="lg:ml-64">{children}</div>
    </>
  );
};

// Composant wrapper conditionnel pour les pages qui peuvent être publiques ou authentifiées
const ConditionalLayout = ({ children }) => {
  const { user, loading } = useAuth();

  // CORRECTION: Attendre que l'authentification soit chargée avant de décider du layout
  // Évite les problèmes de race condition au reload de la page
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (user) {
    // Si connecté, utiliser le layout avec sidebar
    return (
      <>
        <Header />
        <div className="lg:ml-64">{children}</div>
      </>
    );
  }

  // Si non connecté, afficher sans sidebar
  return children;
};

// Composant Layout qui affiche conditionnellement navbar et footer
const Layout = ({ children }) => {
  const location = useLocation();

  // Pages qui n'ont PAS besoin de navbar (mais gardent le footer)
  const noNavbarRoutes = ['/'];

  // Liste des routes qui doivent avoir le layout (navbar + footer)
  // IMPORTANT: "/" est exclu car la page d'accueil (custom ou défaut) a sa propre structure complète
  const layoutRoutes = [
    '/login', '/register', '/forgot-password', '/reset-password', '/verify-2fa',
    '/dashboard', '/sejours', '/enfants', '/inscriptions', '/profil',
    '/mentions-legales', '/politique-confidentialite', '/politique-rgpd-sante', '/cgv',
    '/admin', '/inscription-en-attente'
  ];

  // Vérifier si on est sur une route qui nécessite le layout
  const needsLayout = layoutRoutes.some(route => {
    if (route === '/') return location.pathname === '/';
    return location.pathname.startsWith(route);
  });

  if (!needsLayout) {
    // Pages personnalisées sans layout
    return children;
  }

  // Vérifier si on doit masquer la navbar
  const hideNavbar = noNavbarRoutes.some(route => {
    if (route === '/') return location.pathname === '/';
    return location.pathname.startsWith(route);
  });

  // Pages normales avec navbar et footer
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {!hideNavbar && <Navbar />}
      <div className="flex-grow">
        {children}
      </div>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <NotificationContainer />
          <PromptContainer />
          <ConfirmContainer />
          <Layout>
            <Routes>
              {/* Pages publiques sans sidebar */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/verify-2fa" element={<TwoFactorVerify />} />

              {/* Pages légales (publiques) */}
              <Route path="/mentions-legales" element={<MentionsLegales />} />
              <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
              <Route path="/politique-rgpd-sante" element={<PolitiqueRGPDSante />} />
              <Route path="/cgv" element={<CGV />} />

              {/* Pages authentifiées avec sidebar */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <AuthenticatedLayout>
                      <DashboardRouter />
                    </AuthenticatedLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/sejours"
                element={
                  <ConditionalLayout>
                    <SejoursList />
                  </ConditionalLayout>
                }
              />

              <Route
                path="/enfants"
                element={
                  <PrivateRoute>
                    <AuthenticatedLayout>
                      <EnfantsList />
                    </AuthenticatedLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/inscriptions"
                element={
                  <PrivateRoute>
                    <AuthenticatedLayout>
                      <InscriptionsRouter />
                    </AuthenticatedLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/inscriptions/:id"
                element={
                  <PrivateRoute>
                    <AuthenticatedLayout>
                      <InscriptionDetail />
                    </AuthenticatedLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/paiement/callback"
                element={
                  <PrivateRoute>
                    <AuthenticatedLayout>
                      <PaiementCallback />
                    </AuthenticatedLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/inscriptions/:inscriptionId/informations"
                element={
                  <PrivateRoute>
                    <AuthenticatedLayout>
                      <InformationsEnfant />
                    </AuthenticatedLayout>
                  </PrivateRoute>
                }
              />

              <Route
                path="/admin/sejours"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <GestionSejours />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/partenaires"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <GestionPartenaires />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/validation-partenaires"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <ValidationPartenaires />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/inscriptions/:id"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <AdminInscriptionDetail />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/inscription-en-attente"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <InscriptionEnAttenteValidation />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/membres"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <GestionMembres />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/places"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <GestionPlaces />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/logs"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <LogsAdmin />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/backups"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <BackupsAdmin />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/emails"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <GestionEmails />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/helloasso"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <GestionHelloAsso />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/pages"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <GestionPages />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/documents"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <GestionDocuments />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/pages/accueil"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <EditeurPageAccueil />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/admin/pages/navbar-footer"
                element={
                  <AdminRoute>
                    <AuthenticatedLayout>
                      <EditeurNavbarFooter />
                    </AuthenticatedLayout>
                  </AdminRoute>
                }
              />

              <Route
                path="/profil"
                element={
                  <PrivateRoute>
                    <AuthenticatedLayout>
                      <Profil />
                    </AuthenticatedLayout>
                  </PrivateRoute>
                }
              />

              <Route path="/" element={<RootRouter />} />

              {/* Route pour les documents publics (affichage inline PDF/images) */}
              <Route path="/documents/:slug" element={<DocumentViewer />} />

              {/* Route catch-all pour les pages personnalisées - DOIT être en dernier */}
              <Route path="*" element={<CustomPageViewer />} />
            </Routes>
          </Layout>
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
